import { Check, Link2, Plus, X } from 'lucide-react';
import { useState } from 'react';
import type { Api, CandidateRow, ImportRun, Outcome } from '../lib/api';
import { ApiError } from '../lib/api';
import {
  CANDIDATE_STATUS_LABELS,
  OUTCOME_LABELS,
  REJECT_REASONS,
  RUN_STATUS_LABELS,
} from '../lib/format';
import { btnGhost } from './ui';

const RUN_STYLE: Record<ImportRun['status'], string> = {
  running: 'bg-surface-strong text-fg',
  completed: 'bg-ok-soft text-fg',
  failed: 'bg-danger-soft text-fg',
};

export function RunStatusBadge({ status }: { status: ImportRun['status'] }) {
  return (
    <span
      className={`inline-flex px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.04em] ${RUN_STYLE[status]}`}
    >
      {RUN_STATUS_LABELS[status]}
    </span>
  );
}

export function OutcomeBadge({ outcome }: { outcome?: Outcome | undefined }) {
  if (!outcome) return <span className="text-xs text-muted">—</span>;
  const o = OUTCOME_LABELS[outcome]!;
  return (
    <span
      title={o.hint}
      className={`inline-flex px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.04em] ${o.style}`}
    >
      {o.label}
    </span>
  );
}

export function CandidateStatusText({ row }: { row: CandidateRow }) {
  const label = CANDIDATE_STATUS_LABELS[row.status] ?? row.status;
  if (!row.review) return <span className="text-xs text-muted">{label}</span>;
  const action = { create: 'creato', merge: 'unito', reject: 'scartato' }[row.review.action];
  const auto = row.review.by.sub === 'auto';
  return (
    <span
      className="text-xs text-muted"
      title={[
        auto ? 'Pubblicazione automatica' : (row.review.by.email ?? row.review.by.sub),
        row.review.note,
      ]
        .filter(Boolean)
        .join(' · ')}
    >
      {label} · {action}
      {row.review.reason &&
        ` · ${(REJECT_REASONS[row.review.reason] ?? row.review.reason).toLowerCase()}`}
      {auto && ' in automatico'}
    </span>
  );
}

/** "12 trovati · 1 nuovo · 10 duplicati · 1 incerto" (solo le voci diverse da zero). */
export function RunCountersLine({ run }: { run: ImportRun }) {
  const c = run.counters;
  const parts: string[] = [];
  const add = (n: number | undefined, one: string, many: string) => {
    if (n) parts.push(`${n} ${n === 1 ? one : many}`);
  };
  add(c.items, 'elemento', 'elementi');
  add(c.unchanged, 'invariato', 'invariati');
  add(c.failed, 'errore', 'errori');
  add(c.candidates, 'evento letto', 'eventi letti');
  add(c.new, 'nuovo', 'nuovi');
  add(c.duplicate, 'duplicato', 'duplicati');
  add(c.update, 'aggiornamento', 'aggiornamenti');
  add(c.uncertain, 'incerto', 'incerti');
  add(c.invalid, 'scartato', 'scartati');
  const quality = Object.values(run.quality ?? {})
    .map((q) => q.coverage)
    .filter((v): v is number => typeof v === 'number');
  return (
    <div className="text-xs text-muted">
      {parts.join(' · ') || 'nessun risultato'}
      {quality.length > 0 && (
        <span
          className={quality.some((v) => v < 0.9) ? ' font-medium text-danger' : ''}
          title="Quanto testo del PDF è finito negli eventi: sotto 0,9 l'impaginazione potrebbe essere cambiata"
        >
          {' '}
          · copertura {quality.map((v) => v.toFixed(2).replace('.', ',')).join(', ')}
        </span>
      )}
      {run.error && <div className="text-danger">{run.error}</div>}
    </div>
  );
}

/** Azioni rapide su un candidato da rivedere: quella suggerita in evidenza, le altre a lato. */
export function CandidateActions({
  row,
  api,
  onDone,
}: {
  row: CandidateRow;
  api: Api;
  onDone: (row: CandidateRow, eventId?: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (row.status !== 'needs_review' && row.status !== 'pending') return null;

  const decide = async (body: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ candidate: CandidateRow; eventId?: string }>(
        `/imports/candidates/${row.id}/decision`,
        { method: 'POST', body: { version: row.version, ...body } },
      );
      onDone(r.candidate, r.eventId);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const matchEvent = row.match?.kind === 'event' ? row.match : undefined;
  const create = (
    <button
      key="create"
      disabled={busy || !row.comuneId}
      onClick={() => void decide({ action: 'create', status: 'published' })}
      className={btnGhost}
      title={
        row.comuneId
          ? row.enriched
            ? 'Crea e pubblica l’evento con i testi riscritti'
            : 'Crea e pubblica: i testi non sono ancora stati riscritti, la descrizione resta vuota'
          : 'Comune non riconosciuto: apri il candidato'
      }
    >
      <Plus className="size-4" aria-hidden /> Crea
    </button>
  );
  const merge = matchEvent && (
    <button
      key="merge"
      disabled={busy}
      onClick={() => void decide({ action: 'merge', eventId: matchEvent.id })}
      className={btnGhost}
      title={`Unisci a “${matchEvent.title}”: tiene l’evento e lo completa`}
    >
      <Link2 className="size-4" aria-hidden /> Unisci
    </button>
  );
  const reject = (
    <button
      key="reject"
      disabled={busy}
      onClick={() => void decide({ action: 'reject' })}
      className={btnGhost}
      title="Scarta: non diventa un evento"
    >
      <X className="size-4" aria-hidden /> Scarta
    </button>
  );
  const ordered =
    row.outcome === 'duplicate' || row.outcome === 'update'
      ? [merge, create, reject]
      : [create, merge, reject];
  return (
    <div className="flex flex-wrap items-center justify-end gap-1">
      {ordered.filter(Boolean).map((el, i) => (
        <span key={i} className={i === 0 ? 'bg-surface-strong' : ''}>
          {el}
        </span>
      ))}
      {error && (
        <span role="alert" className="basis-full text-right text-xs text-danger">
          {error}
        </span>
      )}
    </div>
  );
}

export function DoneMark() {
  return <Check className="size-4 text-fg" aria-label="fatto" />;
}
